import {
    echoIsConfigured,
    useConnectionStatus,
    useEcho,
    useEchoPublic,
} from '@laravel/echo-vue';

/**
 * Centralized realtime composable. Wraps the current provider (e.g. Laravel Echo)
 * so the app can switch backends by updating only this module.
 *
 * Returns connectionStatus and channel composables (usePrivateChannel, usePublicChannel).
 * Channel composables must be called during component setup.
 */
export function useRealtime() {
    const configured = echoIsConfigured();

    function usePrivateChannel<TEventPayload>(
        channelName: string,
        events: string | string[],
        callback: (payload: TEventPayload) => void,
    ) {
        if (!configured) throw new Error('Real time is not configured');

        const { leave, stopListening, listen } = useEcho<TEventPayload>(
            channelName,
            events,
            callback,
        );

        return {
            leave,
            stopListening,
            listen,
        };
    }

    function usePublicChannel<TEventPayload>(
        channelName: string,
        events: string | string[],
        callback: (payload: TEventPayload) => void,
    ) {
        if (!configured) throw new Error('Real time is not configured');

        const { leave, stopListening, listen } = useEchoPublic(
            channelName,
            events,
            callback,
        );

        return {
            listen,
            leave,
            stopListening,
        };
    }

    return {
        connectionStatus: useConnectionStatus(),
        usePrivateChannel,
        usePublicChannel,
    };
}
